import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LinkIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  CpuChipIcon,
  ComputerDesktopIcon,
  ClockIcon,
  ArrowPathIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import axiosInstance from '../../../utils/axios';

// Tab configuration
const TABS = [
  { id: 'emails', label: 'Emails', icon: EnvelopeIcon },
  { id: 'ips', label: 'IPs', icon: GlobeAltIcon },
  { id: 'devices', label: 'Devices', icon: CpuChipIcon },
  { id: 'browsers', label: 'Browsers', icon: ComputerDesktopIcon },
];

// Country flag
function getCountryFlag(countryCode) {
  if (!countryCode || countryCode.length !== 2) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

// Association item component
function AssociationItem({ item, type, onClick }) {
  const navigate = useNavigate();

  const handleClick = () => {
    let entityType, entityId;
    switch (type) {
      case 'emails':
        entityType = 'email';
        entityId = item.email;
        break;
      case 'ips':
        entityType = 'ip';
        entityId = item.ip;
        break;
      case 'devices':
        entityType = 'device';
        entityId = item.serial;
        break;
      default:
        return;
    }
    navigate(`/dashboard/logs/activity/${entityType}/${encodeURIComponent(entityId)}`);
  };

  const isClickable = ['emails', 'ips', 'devices'].includes(type);

  return (
    <div
      onClick={isClickable ? handleClick : undefined}
      className={`flex items-center justify-between p-3 rounded-lg border border-gray-100 ${
        isClickable
          ? 'hover:bg-blue-50 hover:border-blue-200 cursor-pointer transition-colors'
          : 'bg-gray-50'
      }`}
    >
      <div className="flex-1 min-w-0">
        {/* Email */}
        {type === 'emails' && (
          <>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 truncate">{item.email}</span>
              {item.role && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                  {item.role}
                </span>
              )}
            </div>
            {item.name && <p className="text-xs text-gray-500">{item.name}</p>}
          </>
        )}

        {/* IP */}
        {type === 'ips' && (
          <>
            <div className="flex items-center gap-2">
              <span className="font-mono font-medium text-gray-900">{item.ip}</span>
              {item.geo?.countryCode && (
                <span className="text-sm">{getCountryFlag(item.geo.countryCode)}</span>
              )}
            </div>
            {item.geo?.city && (
              <p className="text-xs text-gray-500">
                {item.geo.city}, {item.geo.country}
              </p>
            )}
          </>
        )}

        {/* Device */}
        {type === 'devices' && (
          <>
            <span className="font-mono font-medium text-gray-900">{item.serial}</span>
            {item.actions?.length > 0 && (
              <p className="text-xs text-gray-500">
                Actions: {item.actions.slice(0, 3).join(', ')}
                {item.actions.length > 3 && '...'}
              </p>
            )}
          </>
        )}

        {/* Browser */}
        {type === 'browsers' && (
          <>
            <span className="font-medium text-gray-900">
              {item.browser} {item.version}
            </span>
            <p className="text-xs text-gray-500">{item.os}</p>
          </>
        )}

        {/* Sessions */}
        {type === 'sessions' && (
          <>
            <span className="font-mono text-sm text-gray-900">{item.sessionId}</span>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>{item.duration}</span>
              <span>•</span>
              <span>{item.eventCount} events</span>
              {item.isActive && (
                <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-xs">
                  Active
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Count and Arrow */}
      <div className="flex items-center gap-2 ml-3">
        <span className="text-sm font-medium text-gray-600">{item.count}</span>
        {isClickable && (
          <ChevronRightIcon className="w-4 h-4 text-gray-400" />
        )}
      </div>
    </div>
  );
}

export default function ActivityAssociations({ entityType, entityId, timeRange = '30d' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('emails');

  useEffect(() => {
    fetchAssociations();
  }, [entityType, entityId, timeRange]);

  const fetchAssociations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(
        `/logs/activity/associations/${entityType}/${encodeURIComponent(entityId)}`,
        { params: { timeRange, limit: 10 } }
      );

      setData(response.data.data);

      // Set first tab with data as active
      const firstTabWithData = TABS.find(
        tab => response.data.data[tab.id]?.length > 0
      );
      if (firstTabWithData) {
        setActiveTab(firstTabWithData.id);
      }
    } catch (err) {
      console.error('Error fetching associations:', err);
      setError(err.response?.data?.error?.message || 'Failed to load associations');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <LinkIcon className="w-5 h-5 text-gray-400" />
          <h3 className="font-medium text-gray-900">Associations</h3>
        </div>
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-gray-400" />
            <h3 className="font-medium text-gray-900">Associations</h3>
          </div>
          <button onClick={fetchAssociations} className="text-blue-600 hover:text-blue-700 text-sm">
            Retry
          </button>
        </div>
        <div className="h-40 flex items-center justify-center text-gray-500">
          {error}
        </div>
      </div>
    );
  }

  // Get counts for tabs
  const tabCounts = TABS.reduce((acc, tab) => {
    acc[tab.id] = data?.[tab.id]?.length || 0;
    return acc;
  }, {});

  const activeData = data?.[activeTab] || [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <LinkIcon className="w-5 h-5 text-cyan-500" />
          <h3 className="font-medium text-gray-900">Associations</h3>
        </div>
        <button
          onClick={fetchAssociations}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const count = tabCounts[tab.id];

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
              {count > 0 && (
                <span className={`text-xs px-1.5 rounded-full ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {activeData.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No {activeTab} associated with this entity</p>
          </div>
        ) : (
          activeData.map((item, idx) => (
            <AssociationItem key={idx} item={item} type={activeTab} />
          ))
        )}
      </div>

      {/* Sessions note */}
      {data?.sessions?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <ClockIcon className="w-4 h-4" />
            <span>{data.sessions.length} session(s) found</span>
            {data.sessions.some(s => s.isActive) && (
              <span className="text-green-600 text-xs">(1 active)</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
